import React, { useMemo } from "react";
import PropTypes from "prop-types";
import LawFirmDashboard from "../../../globals/components/LawFirmDashboard";
import UnpaidBondsCard from "../../../globals/components/UnpaidBondsCard";
import useLawFirmUserHome from "../hooks/useLawFirmUserHome";
import { LF_HOME_VARIANT } from "../lawFirmHomeConfig";
import { mapLawFirmDashboardLinkIcons } from "../../SHARED/components/dashboardLinkIcons";
import HomeShellBase, { buildHomeIndemnityDialogProps } from "../../SHARED/components/HomeShellBase";
import PgHomeIndemnityDialogs from "../../SHARED/components/PgHomeIndemnityDialogs";
import LawFirmUserHomeToolbar from "./LawFirmUserHomeToolbar";
import RecentActivitySection from "./RecentActivitySection";
import AssignmentAlertsSection from "./AssignmentAlertsSection";
import PendingSignaturesSection from "./PendingSignaturesSection";
import DraftBondRequestsSection from "./DraftBondRequestsSection";
import LawFirmSelectionDialog from "./LawFirmSelectionDialog";
import AddAnotherLawFirmDialog from "./AddAnotherLawFirmDialog";

/** Law-firm home orchestration shell — data in {@link useLawFirmUserHome}. */
export default function LawFirmHomeBase({ variant = LF_HOME_VARIANT.user }) {
  const h = useLawFirmUserHome(variant);
  const links = useMemo(
    () => mapLawFirmDashboardLinkIcons(h.linkGroups),
    [h.linkGroups]
  );

  const { cfg } = h;

  const toolbar = cfg.showAddFirmWorkflow ? (
    <LawFirmUserHomeToolbar
      isLegalService={h.isLegalService}
      setShowAddFirmDialog={h.setShowAddFirmDialog}
      fetchLawFirmDetails={h.fetchLawFirmDetails}
    />
  ) : (
    h.isLegalService && (
      <LawFirmUserHomeToolbar
        isLegalService={h.isLegalService}
        fetchLawFirmDetails={h.fetchLawFirmDetails}
        showAddFirmButton={false}
      />
    )
  );

  return (
    <>
      <HomeShellBase
        loading={h.loading}
        header={toolbar}
        dashboard={<LawFirmDashboard title={cfg.dashboardTitle} links={links} />}
        leftColumn={
          <RecentActivitySection
            loadingActivities={h.loadingActivities}
            selectedActivity={h.selectedActivity}
            setSelectedActivity={h.setSelectedActivity}
            activities={h.activities}
            MAX_DESC_LEN={h.MAX_DESC_LEN}
            getActivityText={h.getActivityText}
            renderHighlighted={h.renderHighlighted}
            formatRelativeTime={h.formatRelativeTime}
            hasMoreActivities={h.hasMoreActivities}
            loadMoreActivities={h.loadMoreActivities}
          />
        }
        rightColumn={
          <>
            <AssignmentAlertsSection
              unassignedBonds={h.unassignedBonds}
              loadingUnassigned={h.loadingUnassigned}
              getBondUrl={(bond) =>
                h.getBondUrl(bond?.bond_type, bond?.bond_id ?? bond?.id)
              }
              formatRelativeTime={h.formatRelativeTime}
            />
            <PendingSignaturesSection
              missingIndemnityBonds={h.missingIndemnityBonds}
              loadingIndemnity={h.loadingIndemnity}
              markingIndemnity={h.markingIndemnity}
              getBondUrl={h.getBondUrl}
              formatRelativeTime={h.formatRelativeTime}
              handleMarkIndemnityClick={h.handleMarkIndemnityClick}
            />
            <DraftBondRequestsSection
              cachedBonds={h.cachedBonds}
              loadingDrafts={h.loadingDrafts}
              getCacheRoute={h.getCacheRoute}
              navigate={h.navigate}
              handleDeleteDraft={h.handleDeleteDraft}
              deletingId={h.deletingId}
              formatRelativeTime={h.formatRelativeTime}
            />
            <UnpaidBondsCard lawFirmId={h.lawFirmIdState} />
          </>
        }
        contentSx={{ px: 3 }}
      />

      <LawFirmSelectionDialog
        showDialog={h.showDialog}
        selectedOption={h.selectedOption}
        setSelectedOption={h.setSelectedOption}
        lawFirmOptions={h.lawFirmOptions}
        handleLawFirmSelection={h.handleLawFirmSelection}
      />

      {cfg.showAddFirmWorkflow && (
        <AddAnotherLawFirmDialog
          showAddFirmDialog={h.showAddFirmDialog}
          setShowAddFirmDialog={h.setShowAddFirmDialog}
          requestResult={h.requestResult}
          searchResults={h.searchResults}
          isSearching={h.isSearching}
          handleSearchFirms={h.handleSearchFirms}
          handleSelectNewFirm={h.handleSelectNewFirm}
          selectedNewFirm={h.selectedNewFirm}
          selectedNewLocation={h.selectedNewLocation}
          firmLocationsNew={h.firmLocationsNew}
          setSelectedNewLocation={h.setSelectedNewLocation}
          requestMessage={h.requestMessage}
          setRequestMessage={h.setRequestMessage}
          handleRequestAccess={h.handleRequestAccess}
          requestingAccess={h.requestingAccess}
        />
      )}

      <PgHomeIndemnityDialogs {...buildHomeIndemnityDialogProps(h.home)} />
    </>
  );
}

LawFirmHomeBase.propTypes = {
  variant: PropTypes.oneOf([LF_HOME_VARIANT.user, LF_HOME_VARIANT.admin]),
};
